import {acceptHMRUpdate, defineStore} from 'pinia';
import {computed, ref} from 'vue';
import type {ActionCommand, ActionDecorator} from '~/lib/types/actionTypes';
import useApi from '~/composables/useApi';
import type {ProblemDetail} from '~/lib/types/fetchErrors';
import {getFilenameFromContentDisposition, saveBlob} from '~/lib/download';
import {parseProblemDetail} from '~/middleware/api';
import {actionCommandFormLabels} from '~/composables/useActionForm';

export const useActionsStore = defineStore('actions', () => {
	const {GET, PATCH, POST, PUT} = useApi();

	const activeRie = ref<string>();
	const actions = ref<ActionDecorator[]>([]);
	const loading = ref(false);
	const problemDetail = ref<ProblemDetail>();
	const waitingForExport = ref(false);

	const getAction = computed(() => (actionId: number) => {
		return actions.value.find((action) => action.id === actionId);
	});

	const clearData = () => {
		actions.value = [];
	};

	const deleteAction = async (rieId: string, actionId: number) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {error} = await POST(`/rie/{rieId}/actions/{actionId}/delete`, {
			params: {
				path: {
					rieId,
					actionId: actionId.toString()
				}
			}
		});

		if (error) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		actions.value = actions.value.filter((action) => action.id !== actionId);

		loading.value = false;
		return true;
	};

	const updateAction = async (data: ActionDecorator) => {
		const actionIndex = actions.value.findIndex((action) => action.id === data.id);
		if (actionIndex === -1) {
			actions.value.push(data);
		} else {
			actions.value[actionIndex] = data;
		}
	};

	const editAction = async (rieId: string, actionCommand: ActionCommand, actionId: number) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await PUT(`/rie/{rieId}/actions/{actionId}`, {
			params: {
				path: {
					rieId,
					actionId: actionId.toString()
				}
			},
			body: actionCommand
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = parseProblemDetail(error, actionCommandFormLabels);
			return false;
		}

		await updateAction(data);

		loading.value = false;
		return true;
	};

	const updateActionStatus = async (rieId: string, actionId: number, status: ActionDecorator['status']) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await PATCH('/rie/{rieId}/actions/{actionId}', {
			params: {
				path: {
					rieId,
					actionId: actionId.toString()
				}
			},
			body: status
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = parseProblemDetail(error, actionCommandFormLabels);
			return false;
		}

		await updateAction(data);

		loading.value = false;
		return true;
	};

	const submitAction = async (rieId: string, action: ActionCommand) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await POST('/rie/{rieId}/actions', {
			params: {
				path: {
					rieId: rieId.toString()
				}
			},
			body: action
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = parseProblemDetail(error, actionCommandFormLabels);
			return false;
		}

		await updateAction(data);

		loading.value = false;
		return true;
	};

	const exportActionsToExcel = async (rieId: string, filename: string) => {
		if (waitingForExport.value) {
			return false;
		}
		waitingForExport.value = true;
		problemDetail.value = undefined;

		const {data, error} = await GET('/rie/{rieId}/actions/export/excel', {
			params: {
				path: {
					rieId
				}
			},
			parseAs: 'blob'
		});

		if (error || !data) {
			waitingForExport.value = false;
			problemDetail.value = error;
			return false;
		}

		saveBlob(data, filename);
		waitingForExport.value = false;
	};

	const exportActionsToPDF = async (rieId: string, actionIds: Array<ActionDecorator['id']>) => {
		if (waitingForExport.value) {
			return false;
		}
		waitingForExport.value = true;
		problemDetail.value = undefined;

		const {data, error, response} = await POST('/rie/{rieId}/actions/export/pdf', {
			params: {
				path: {
					rieId
				}
			},
			body: actionIds,
			parseAs: 'blob'
		});

		if (error || !data) {
			problemDetail.value = error;
			waitingForExport.value = false;
			return false;
		}

		const filename = getFilenameFromContentDisposition(response, 'Plan van Aanpak.pdf');
		saveBlob(data, filename);
		waitingForExport.value = false;
	};

	const fetchActionOverview = async (rieId: string) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET('/rie/{rieId}/actions', {
			params: {
				path: {
					rieId: rieId.toString()
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		actions.value = data;

		loading.value = false;
	};

	const fetchAction = async (rieId: string, actionId: number) => {
		loading.value = true;
		problemDetail.value = undefined;

		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		const {data, error} = await GET(`/rie/{rieId}/actions/{actionId}`, {
			params: {
				path: {
					rieId,
					actionId: actionId.toString()
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		await updateAction(data);

		loading.value = false;
	};

	return {
		actions,
		problemDetail,
		loading,
		getAction,
		waitingForExport,
		deleteAction,
		fetchActionOverview,
		fetchAction,
		submitAction,
		editAction,
		updateActionStatus,
		clearData,
		exportActionsToExcel,
		exportActionsToPDF
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useActionsStore, import.meta.hot));
}
